import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { formatRatioCompact, getRatioColorClass } from "./utils";

const CONTENT_FIELDS = [
  { key: "summary", label: "Summary" },
  { key: "hook", label: "Hook" },
  { key: "format", label: "Format" },
];

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (e) {
    return null;
  }
};

const getEmbedUrl = (video) => {
  if (!video?.platform_id) return null;
  if (video.platform === "instagram") {
    return `https://www.instagram.com/p/${video.platform_id}/embed?hidecaption=false&maxwidth=320`;
  }
  if (video.platform === "youtube") {
    return `https://www.youtube.com/embed/${video.platform_id}`;
  }
  return null;
};

const buildOutlierTooltip = (metric) =>
  `${metric.charAt(0).toUpperCase() + metric.slice(1)} Outlier Score: How this video performs vs this creator's typical videos. Above 1x = better than their usual; below 1x = underperforming.`;

const StatIcon = ({ type, className = "w-4 h-4" }) => {
  const iconClass = `${className} text-theme-text opacity-60`;
  if (type === "views") {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={iconClass} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    );
  }
  if (type === "likes") {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={iconClass} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
      </svg>
    );
  }
  if (type === "comments") {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={iconClass} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
      </svg>
    );
  }
  return null;
};

const StatItem = ({ icon, display, outlierRatio, tooltipContent, tooltipId }) => {
  const compact = formatRatioCompact(outlierRatio);
  const colorClass = compact ? getRatioColorClass(outlierRatio) : "text-theme-text opacity-40";
  return (
    <span className="flex items-end gap-1.5 text-sm">
      <StatIcon type={icon} />
      <span className="text-theme-text font-medium">{display}</span>
      <span
        data-tooltip-id={tooltipId}
        data-tooltip-content={tooltipContent}
        className={`text-xs font-medium cursor-help ${colorClass}`}
      >
        {compact?.text ?? "—"}
      </span>
    </span>
  );
};

const SampleVideoRow = ({
  video,
  creator,
  creatorViewsOutlier,
  creatorLikesOutlier,
  creatorCommentsOutlier,
  content = {},
  formatNumber,
  videoDetailUrl,
}) => {
  const [fieldsExpanded, setFieldsExpanded] = useState(false);
  const [iframeInView, setIframeInView] = useState(false);
  const embedContainerRef = useRef(null);

  useEffect(() => {
    const el = embedContainerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIframeInView(true);
      },
      { rootMargin: "100px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const embedUrl = getEmbedUrl(video);
  const dateStr = formatDate(video?.uploaded_at);
  const creatorName = creator?.name || creator?.platform_id;
  const subtitle = [creatorName, dateStr].filter(Boolean).join(" · ");

  const fmt = (val) => {
    if (val == null) return "—";
    if (formatNumber) return formatNumber(val);
    return String(val);
  };
  const rowId = video?.id ?? "row";
  const stats = [
    {
      icon: "views",
      display: fmt(video?.count_views),
      outlier: creatorViewsOutlier,
      tooltipContent: buildOutlierTooltip("views"),
      tooltipId: `sample-row-${rowId}-views`,
    },
    {
      icon: "likes",
      display: fmt(video?.count_likes),
      outlier: creatorLikesOutlier,
      tooltipContent: buildOutlierTooltip("likes"),
      tooltipId: `sample-row-${rowId}-likes`,
    },
    {
      icon: "comments",
      display: fmt(video?.count_comments),
      outlier: creatorCommentsOutlier,
      tooltipContent: buildOutlierTooltip("comments"),
      tooltipId: `sample-row-${rowId}-comments`,
    },
  ];

  return (
    <div className="flex flex-col md:flex-row border border-theme-secondary rounded-lg bg-theme-secondary overflow-hidden">
      <div
        ref={embedContainerRef}
        className="w-full md:w-1/4 flex-shrink-0 aspect-video md:aspect-auto md:h-[350px] bg-theme-main"
      >
        {embedUrl ? (
          <iframe
            src={iframeInView ? embedUrl : undefined}
            className="w-full h-full"
            frameBorder="0"
            allowFullScreen
            scrolling="no"
            title={video?.title || "Video"}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-theme-text opacity-40 text-sm">
            No preview
          </div>
        )}
      </div>

      <div
        className={`flex-1 min-w-0 p-4 flex flex-col gap-3 min-h-0 md:min-h-[350px] ${!fieldsExpanded ? "md:max-h-[350px] md:overflow-hidden" : ""}`}
      >
        <div>
          {videoDetailUrl ? (
            <Link
              to={videoDetailUrl}
              className="text-sm font-semibold text-theme-text hover:underline line-clamp-2"
            >
              {video?.title || "Untitled"}
            </Link>
          ) : (
            <span className="text-sm font-semibold text-theme-text line-clamp-2">
              {video?.title || "Untitled"}
            </span>
          )}
          {subtitle && (
            <p className="text-xs text-theme-text opacity-60 mt-0.5">{subtitle}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {stats.map((stat) => (
            <StatItem
              key={stat.icon}
              icon={stat.icon}
              display={stat.display}
              outlierRatio={stat.outlier}
              tooltipContent={stat.tooltipContent}
              tooltipId={stat.tooltipId}
            />
          ))}
        </div>
        {stats.map((stat) => (
          <Tooltip key={stat.icon} id={stat.tooltipId} place="top" className="!max-w-[260px] !text-xs" />
        ))}

        <div className="flex-1 relative min-h-0 flex flex-col">
          <div
            className={!fieldsExpanded ? "flex-1 min-h-0 overflow-hidden cursor-pointer" : ""}
            onClick={!fieldsExpanded ? () => setFieldsExpanded(true) : undefined}
            role={!fieldsExpanded ? "button" : undefined}
            aria-expanded={fieldsExpanded}
          >
            <div className="space-y-2">
              {CONTENT_FIELDS.map((field, idx) => (
                <div key={field.key} className={idx === 0 ? "" : "pt-2 border-t border-theme-secondary"}>
                  <div className="text-[10px] uppercase tracking-wider text-theme-text opacity-60">{field.label}</div>
                  <div className="text-sm text-theme-text whitespace-pre-wrap break-words mt-0.5">
                    {content[field.key] ? (
                      String(content[field.key]).trim()
                    ) : (
                      <span className="opacity-50">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {!fieldsExpanded && (
            <div
              className="absolute inset-x-0 bottom-0 h-14 pointer-events-none bg-gradient-to-t from-theme-secondary via-theme-secondary/80 to-transparent"
              aria-hidden
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SampleVideoRow;
